import { Dialog } from "@kobalte/core/dialog";
import { InvitationsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import type { ProjectId } from "@storytell/types";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbLoader2, TbX } from "solid-icons/tb";
import { type Component, createMemo, createResource, Show, Suspense } from "solid-js";
import { useI18n } from "~/i18n/context";
import { lazy } from "~/lib/lazyWithReload";
import { useWire } from "~/wire";

// Split here rather than at the call sites: every surface carrying a project switcher has a
// static edge to this modal, so one eager importer would keep the section in the shell's chunk.
// AnimateChildren renders nothing until the modal first opens, so no extra mount gate is needed.
const ProjectShareSection = lazy(() =>
  import("./ProjectShareSection").then((m) => ({ default: m.ProjectShareSection }))
);

interface ShareProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

export const ShareProjectModal: Component<ShareProjectModalProps> = (props) => {
  const wire = useWire();
  const client = useStorytellClient();
  const { t, currentLanguage } = useI18n();

  // Ensure reactivity by tracking language changes
  createMemo(() => currentLanguage());

  const project = () => wire.projects?.get?.(props.projectId as ProjectId);

  // Fetch invitations for this project
  const [allInvitations, { refetch: refetchInvitations, mutate: mutateInvitations }] = createResource(
    () => props.isOpen && wire.auth?.isReady?.() && wire.auth?.userId?.() && project()?.id,
    async () => {
      // Safety check: if auth isn't ready or token isn't available, return empty array
      if (!wire.auth?.isReady?.() || !wire.auth?.token) {
        return [];
      }
      const request = InvitationsSyncRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, undefined, undefined);

      if (response.isErr()) {
        return [];
      }

      return Object.values(response.value.invites);
    },
    {
      deferStream: true,
      initialValue: [],
    }
  );

  const invitations = () => allInvitations()?.filter((invite) => invite.projectId === props.projectId);

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-90 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-90 flex items-center justify-center p-4">
              <Dialog.Content class="relative max-h-[85vh] w-screen max-w-4xl overflow-y-auto rounded-xl bg-panel text-primary-text">
                <div class="sticky top-0 z-10 flex items-center justify-between border-b border-primary-border bg-panel px-6 py-4">
                  <Dialog.Title class="text-lg font-semibold">{t("share_section_title")}</Dialog.Title>
                  <Dialog.CloseButton as={StButton} class="p-1">
                    <TbX class="size-5" />
                  </Dialog.CloseButton>
                </div>

                <div class="p-6">
                  <Show
                    when={wire.projects?.get?.(props.projectId as ProjectId)?.grants?.["op.update"]}
                    fallback={<p class="py-8 text-center text-muted-text">{t("error_permission_generic_message")}</p>}
                  >
                    <Suspense
                      fallback={
                        <div class="flex justify-center py-8">
                          <TbLoader2 class="size-6 animate-spin text-secondary-text" />
                        </div>
                      }
                    >
                      <ProjectShareSection
                        projectId={props.projectId}
                        invitations={invitations}
                        refetchInvitations={() => void refetchInvitations()}
                        mutateInvitations={mutateInvitations}
                        embedded={true}
                      />
                    </Suspense>
                  </Show>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
